// Courrier de questions et de réserves avant remise de l'offre, ou avis sur devis reçu : édition du brouillon (enregistrement automatique),
// émission qui fige le courrier (numéro, PDF), envoi renseigné à la main, nouvelle version. L'application n'envoie aucun courriel.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CopyPlus, FileDown, Printer, RefreshCw, RotateCcw, Send, Stamp } from 'lucide-react';
import { api, ErreurApi, messageErreur } from '../../lib/api.ts';
import { date, dateHeure, pluriel } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Chargement, Champ, ChampChoix, ChampTexte, Dialogue, EtatErreur, IndicateurEnregistrement, Panneau,
  useConfirmation, useNotifier, type EtatEnregistrement,
} from '../../components/ui.tsx';
import { DossierCadre } from './DossierCadre.tsx';
import { BadgePriorite, Extrait, LienSource, TYPES_PIECE, options } from './commun.tsx';

// ─── Contrat de l'API (server/modules/etudes/courriers.ts) ───
export interface SourceCourrier { version_id: string | null; titre: string; doc_type: string | null; version_label: string | null; page: number | null; locator: string | null; excerpt: string | null }
interface ItemCourrier {
  id: string; section: string; position: number; inclus: boolean; texte: string; texte_origine: string; modifie: boolean; numero: string | null;
  sources: SourceCourrier[]; ia: boolean; modifie_le: string | null; modifie_par_nom: string | null;
  /** Priorité pour le destinataire (null : point préparé avant les priorités) et pourquoi (ou avec quel point il a été regroupé). */
  priorite?: string | null; priorite_motif?: string | null;
}
interface Courrier {
  id: string; dossier_id: string; numero: string | null; status: 'brouillon' | 'emis'; version_no: number; modele?: string;
  dest_nom: string | null; dest_qualite: string | null; dest_societe: string | null; dest_adresse: string | null; dest_email: string | null;
  objet: string; reference_dossier: string | null; reference_destinataire: string | null; date_courrier: string;
  introduction: string; conclusion: string; signataire_nom: string | null; signataire_qualite: string | null;
  sections: Record<string, boolean>; emis_le: string | null; emis_par_nom: string | null;
  envoye_le: string | null; envoye_moyen: string | null; envoye_precision: string | null; envoye_par_nom: string | null;
  contient_ia: boolean; updated_at: string;
  precedent: { id: string; numero: string | null } | null; suivant: { id: string; numero: string | null; status: string } | null;
}
interface VueCourrier {
  courrier: Courrier; items: ItemCourrier[];
  pieces: { titre: string; type: string; version: string; date: string | null; date_libelle: string; illisible: boolean }[];
  organisation: { nom: string; coordonneesRenseignees: boolean };
  sectionsInfo: { cle: string; titre: string; prefixe: string; chapeau: string }[];
  modele?: { cle: string; libelle: string; aide: string };
  defauts: { introduction: string; conclusion: string; objet: string };
  droits: { ecrire: boolean; coordonnees: boolean };
  priorites?: { bloquant: number; important: number; complement: number; complementsDecoches: number };
}
export interface CourrierListe {
  id: string; numero: string | null; status: 'brouillon' | 'emis'; version_no: number; modele?: string; date_courrier: string; dest_nom: string | null; dest_societe: string | null;
  dest_qualite: string | null; envoye_le: string | null; envoye_moyen: string | null; emis_le: string | null; created_at: string; precedent_numero: string | null; nb_points: number;
}

export const QUALITES: Record<string, string> = {
  architecte: 'Architecte', maitre_oeuvre: 'Maître d’œuvre', maitre_ouvrage: 'Maître d’ouvrage', client: 'Client', autre: 'Autre',
};
/** Modèles de courrier (contrat : MODELES_INFO de server/modules/etudes/courrier-contenu.ts). */
export const MODELES: Record<string, { libelle: string; aide: string }> = {
  questions_offre: { libelle: 'Questions et réserves avant remise de notre offre',
    aide: 'Vous répondez à une consultation : questions au maître d’œuvre ou au client avant de remettre votre offre.' },
  avis_devis: { libelle: 'Avis sur devis reçu',
    aide: 'Le client vous a transmis le devis d’une autre entreprise : points à faire préciser par cette entreprise et réserves, adressés au client.' },
};
export const MOYENS: Record<string, string> = {
  courriel: 'Courriel', courrier: 'Courrier postal', recommande: 'Lettre recommandée', remise_main: 'Remise en main propre',
  plateforme: 'Plateforme de consultation', autre: 'Autre',
};

type Champs = {
  destNom: string; destQualite: string; destSociete: string; destAdresse: string; destEmail: string; objet: string; referenceDossier: string;
  referenceDestinataire: string; dateCourrier: string; introduction: string; conclusion: string; signataireNom: string; signataireQualite: string;
};
const depuisServeur = (c: Courrier): Champs => ({
  destNom: c.dest_nom ?? '', destQualite: c.dest_qualite ?? '', destSociete: c.dest_societe ?? '', destAdresse: c.dest_adresse ?? '',
  destEmail: c.dest_email ?? '', objet: c.objet, referenceDossier: c.reference_dossier ?? '', referenceDestinataire: c.reference_destinataire ?? '',
  dateCourrier: c.date_courrier, introduction: c.introduction, conclusion: c.conclusion, signataireNom: c.signataire_nom ?? '',
  signataireQualite: c.signataire_qualite ?? '',
});


export function PageCourrier() {
  const { id = '', cid = '' } = useParams();
  const q = useQuery({ queryKey: ['courrier', cid], queryFn: () => api.get<VueCourrier>(`/api/courriers/${cid}`) });
  if (q.isLoading) return <DossierCadre dossierId={id} actif="ecarts" large><Chargement lignes={6} /></DossierCadre>;
  if (q.isError || !q.data) return <DossierCadre dossierId={id} actif="ecarts" large><EtatErreur erreur={q.error} reessayer={() => q.refetch()} /></DossierCadre>;
  return <EditeurCourrier key={`${q.data.courrier.id}-${q.data.courrier.status}`} dossierId={id} vue={q.data} />;
}

function EditeurCourrier({ dossierId, vue }: { dossierId: string; vue: VueCourrier }) {
  const c = vue.courrier;
  const qc = useQueryClient();
  const naviguer = useNavigate();
  const notifier = useNotifier();
  const { confirmer, element: confirmation } = useConfirmation();
  const modifiable = vue.droits.ecrire && c.status === 'brouillon';
  const [champs, setChamps] = useState<Champs>(() => depuisServeur(c));
  const [etat, setEtat] = useState<EtatEnregistrement>('enregistre');
  const [quand, setQuand] = useState<string>();
  const [erreursEmission, setErreursEmission] = useState<{ champ: string; message: string }[]>([]);
  const [action, setAction] = useState<string | null>(null);
  const attente = useRef<Partial<Record<keyof Champs, string>>>({});
  const minuterie = useRef<ReturnType<typeof setTimeout>>(undefined);
  const cle = ['courrier', c.id];

  const recharger = () => qc.invalidateQueries({ queryKey: cle });
  const rafraichirListes = () => {
    qc.invalidateQueries({ queryKey: ['etudes-courriers', dossierId] });
    qc.invalidateQueries({ queryKey: ['etudes-questions', dossierId] });
    qc.invalidateQueries({ queryKey: ['etudes-synthese', dossierId] });
  };

  // Enregistrement automatique des champs d'en-tête (regroupés, 700 ms après la dernière frappe).
  const vider = async () => {
    clearTimeout(minuterie.current);
    const corps = attente.current;
    if (!Object.keys(corps).length) return true;
    attente.current = {};
    setEtat('en_cours');
    try {
      await api.patch(`/api/courriers/${c.id}`, corps);
      setEtat('enregistre'); setQuand(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
      return true;
    } catch (e) {
      attente.current = { ...corps, ...attente.current };
      setEtat('erreur'); notifier(messageErreur(e), true);
      return false;
    }
  };
  useEffect(() => () => { void vider(); }, []);
  const changer = (k: keyof Champs, v: string) => {
    setChamps((x) => ({ ...x, [k]: v }));
    if (!modifiable) return;
    // Les champs obligatoires vides ne sont pas envoyés (ils resteraient invalides) : on garde la dernière valeur enregistrée.
    if (['objet', 'introduction', 'conclusion', 'dateCourrier'].includes(k) && !v.trim()) { setEtat('modifie'); return; }
    attente.current[k] = v;
    setEtat('modifie');
    clearTimeout(minuterie.current);
    minuterie.current = setTimeout(() => { void vider(); }, 700);
  };

  const basculerSection = async (s: string, v: boolean) => {
    try { await api.patch(`/api/courriers/${c.id}`, { sections: { [s]: v } }); await recharger(); } catch (e) { notifier(messageErreur(e), true); }
  };

  const executer = async (nom: string, fn: () => Promise<void>) => {
    setAction(nom);
    try { await fn(); } catch (e) {
      if (e instanceof ErreurApi && Array.isArray(e.details)) setErreursEmission(e.details as any);
      notifier(messageErreur(e), true);
    } finally { setAction(null); }
  };

  const emettre = () => executer('emettre', async () => {
    if (!(await vider())) return;
    const ok = await confirmer('Émettre le courrier ?',
      'Le courrier reçoit un numéro et devient définitif : il ne pourra plus être modifié. Pour le corriger ensuite, vous créerez une nouvelle version qui l’annule et le remplace.',
      'Émettre le courrier');
    if (!ok) return;
    const r = await api.post<VueCourrier>(`/api/courriers/${c.id}/emettre`, {});
    setErreursEmission([]);
    qc.setQueryData(cle, r);
    rafraichirListes();
    notifier(`Courrier ${r.courrier.numero} émis. Téléchargez le PDF pour l’envoyer.`);
  });

  const actualiser = () => executer('actualiser', async () => {
    const r = await api.post<{ ajoutes: number }>(`/api/courriers/${c.id}/actualiser`, {});
    await recharger();
    notifier(r.ajoutes ? `${pluriel(r.ajoutes, 'nouveau point ajouté', 'nouveaux points ajoutés')}.` : 'Aucun nouveau point dans le dossier.');
  });

  const nouvelleVersion = () => executer('version', async () => {
    const r = await api.post<{ id: string; existait: boolean }>(`/api/courriers/${c.id}/nouvelle-version`, {});
    rafraichirListes();
    notifier(r.existait ? 'Une nouvelle version est déjà en préparation : elle est ouverte.' : 'Nouvelle version créée en brouillon.');
    naviguer(`/etudes/${dossierId}/courriers/${r.id}`);
  });

  const supprimer = () => executer('supprimer', async () => {
    const ok = await confirmer('Supprimer ce brouillon ?', 'Le brouillon et ses modifications seront supprimés. Le dossier n’est pas modifié.', 'Supprimer le brouillon');
    if (!ok) return;
    clearTimeout(minuterie.current); attente.current = {};
    await api.del(`/api/courriers/${c.id}`);
    rafraichirListes();
    notifier('Brouillon supprimé.');
    naviguer(`/etudes/${dossierId}/ecarts?onglet=courriers`);
  });

  const retenus = vue.items.filter((i) => i.numero).length;
  // En-tête : consulter d'abord (PDF, version imprimable). L'émission, définitive, est séparée (panneau d'état) et confirmée.
  const actions = (
    <>
      <a className="btn btn-principal" href={`/api/courriers/${c.id}/courrier.pdf`} target="_blank" rel="noopener"><FileDown aria-hidden />PDF</a>
      <a className="btn" href={`/api/courriers/${c.id}/courrier.html`} target="_blank" rel="noopener"><Printer aria-hidden />Version imprimable</a>
      {c.status === 'emis' && vue.droits.ecrire && !c.suivant && (
        <Bouton icone={<CopyPlus aria-hidden />} chargement={action === 'version'} onClick={nouvelleVersion}>Nouvelle version</Bouton>)}
    </>
  );
  const modele = vue.modele ?? { cle: c.modele ?? 'questions_offre', ...(MODELES[c.modele ?? 'questions_offre'] ?? MODELES.questions_offre) };

  return (
    <DossierCadre dossierId={dossierId} actif="ecarts" large actions={actions}>
      <div className="co-page">
        <aside className="co-cote pile" aria-label="État du courrier">
          <Panneau titre={c.status === 'emis' ? `Courrier ${c.numero}` : 'Courrier en préparation'}
            actions={modifiable ? <IndicateurEnregistrement etat={etat} quand={quand} /> : undefined}>
            <div className="pile" style={{ gap: 10 }}>
              <div className="rangee" style={{ gap: 8, flexWrap: 'wrap' }}>
                {c.status === 'emis' ? <Badge ton="succes">Émis — figé</Badge> : <Badge ton="ambre">Brouillon</Badge>}
                {c.version_no > 1 && <Badge ton="neutre" icone={false}>Version {c.version_no}</Badge>}
                <span className="petit secondaire">{pluriel(retenus, 'point retenu', 'points retenus')}</span>
              </div>
              <p className="petit"><strong>Modèle :</strong> {modele.libelle}</p>
              {c.status === 'emis' && <p className="petit secondaire">Émis le {dateHeure(c.emis_le)}{c.emis_par_nom ? ` par ${c.emis_par_nom}` : ''}. Il ne peut plus être modifié.</p>}
              {c.status === 'brouillon' && <p className="petit secondaire">Chaque modification est enregistrée automatiquement. L’émission attribue un numéro et fige le courrier.</p>}
              {c.precedent && <p className="petit">Annule et remplace : <Link to={`/etudes/${dossierId}/courriers/${c.precedent.id}`}>courrier {c.precedent.numero ?? 'précédent'}</Link></p>}
              {c.suivant && <p className="petit">Remplacé par : <Link to={`/etudes/${dossierId}/courriers/${c.suivant.id}`}>{c.suivant.numero ? `courrier ${c.suivant.numero}` : 'une nouvelle version en préparation'}</Link></p>}
              {modifiable && (
                <div className="groupe-btn">
                  <Bouton compact icone={<RefreshCw aria-hidden />} chargement={action === 'actualiser'} onClick={actualiser}>Ajouter les nouveaux points du dossier</Bouton>
                  <Bouton compact variante="fantome" chargement={action === 'supprimer'} onClick={supprimer}>Supprimer le brouillon</Bouton>
                </div>
              )}
              {modifiable && (
                <div className="co-emission pile" style={{ gap: 8 }}>
                  <p className="petit secondaire">Relisez le PDF avant d’émettre : l’émission attribue un numéro et rend le courrier définitif (une confirmation est demandée).</p>
                  <Bouton variante="principal" icone={<Stamp aria-hidden />} chargement={action === 'emettre'} onClick={emettre}>Émettre le courrier…</Bouton>
                </div>
              )}
            </div>
          </Panneau>
          {c.status === 'emis' && <PanneauEnvoi c={c} ecrire={vue.droits.ecrire} recharger={recharger} rafraichirListes={rafraichirListes} />}
        </aside>

        <div className="co-principal pile-l">
          {erreursEmission.length > 0 && (
            <Alerte ton="danger" titre="Courrier incomplet : émission impossible">
              <ul className="et-liste-simple">{erreursEmission.map((e, i) => <li key={i}>{e.message}</li>)}</ul>
            </Alerte>
          )}
          {!vue.organisation.coordonneesRenseignees && <AlerteCoordonnees modifiable={vue.droits.coordonnees} recharger={recharger} />}
          {c.contient_ia && (
            <Alerte ton="ambre" titre="Points issus de l’assistance">
              Certains points retenus proviennent d’une proposition de l’assistance : relisez-les. Le PDF portera en pied de dernière page la mention
              « Document préparé avec l’aide d’un outil d’analyse automatisé et relu par {champs.signataireNom || 'le signataire'} ».
            </Alerte>
          )}

          <Panneau titre="Destinataire">
            <div className="grille-form">
              <Champ libelle="Nom" value={champs.destNom} readOnly={!modifiable} autoComplete="off" erreur={erreursEmission.find((e) => e.champ === 'destNom')?.message}
                onChange={(e) => changer('destNom', e.target.value)} />
              <ChampChoix libelle="Qualité" value={champs.destQualite} disabled={!modifiable} vide="À préciser" options={options(QUALITES)}
                erreur={erreursEmission.find((e) => e.champ === 'destQualite')?.message}
                onChange={(e) => { changer('destQualite', e.target.value); }} />
              <Champ libelle="Société" value={champs.destSociete} readOnly={!modifiable} autoComplete="off" onChange={(e) => changer('destSociete', e.target.value)} />
              <Champ libelle="Adresse électronique" type="email" value={champs.destEmail} readOnly={!modifiable} autoComplete="off"
                erreur={erreursEmission.find((e) => e.champ === 'destEmail')?.message} onChange={(e) => changer('destEmail', e.target.value)} />
            </div>
            <div style={{ marginTop: 'var(--e-4)' }}>
              <ChampTexte libelle="Adresse postale" rows={3} value={champs.destAdresse} readOnly={!modifiable} onChange={(e) => changer('destAdresse', e.target.value)} />
            </div>
          </Panneau>

          <Panneau titre="Références">
            <div className="pile">
              <Champ libelle="Objet" value={champs.objet} readOnly={!modifiable} onChange={(e) => changer('objet', e.target.value)}
                aide={!champs.objet.trim() ? 'L’objet est obligatoire.' : undefined} />
              <div className="grille-form">
                <Champ libelle="Notre référence" value={champs.referenceDossier} readOnly={!modifiable} onChange={(e) => changer('referenceDossier', e.target.value)} />
                <Champ libelle="Votre référence" aide="N° de consultation ou de marché, si connu" value={champs.referenceDestinataire} readOnly={!modifiable}
                  onChange={(e) => changer('referenceDestinataire', e.target.value)} />
                <Champ libelle="Date du courrier" type="date" value={champs.dateCourrier} readOnly={!modifiable} onChange={(e) => changer('dateCourrier', e.target.value)} />
              </div>
            </div>
          </Panneau>

          <Panneau titre="Introduction" actions={modifiable && champs.introduction !== vue.defauts.introduction
            ? <Bouton compact variante="fantome" icone={<RotateCcw aria-hidden />} onClick={() => changer('introduction', vue.defauts.introduction)}>Texte par défaut</Bouton> : undefined}>
            <ChampTexte libelle="Paragraphe d’introduction" rows={6} value={champs.introduction} readOnly={!modifiable} onChange={(e) => changer('introduction', e.target.value)} />
          </Panneau>

          <Panneau titre={`Pièces examinées (${vue.pieces.length})`}>
            {vue.pieces.length === 0 ? <p className="secondaire">Aucune pièce lisible dans le dossier : déposez les pièces avant de préparer le courrier.</p> : (
              <ul className="et-liste-simple">
                {vue.pieces.map((p, i) => (
                  <li key={i}><strong className="petit">{p.titre}</strong> <span className="petit secondaire">— {p.type}, version {p.version}{p.date ? `, ${p.date_libelle} ${date(p.date)}` : ''}</span>
                    {p.illisible && <> <Badge ton="danger">Illisible en tout ou partie</Badge></>}</li>
                ))}
              </ul>
            )}
            <p className="tres-petit discret" style={{ marginTop: 8 }}>Liste reprise automatiquement des pièces du dossier.</p>
          </Panneau>

          {vue.priorites && vue.priorites.bloquant + vue.priorites.important + vue.priorites.complement > 0 && (
            <Alerte ton="info" titre="Points triés par importance pour le destinataire">
              {pluriel(vue.priorites.bloquant, 'point bloquant', 'points bloquants')} (à obtenir avant de signer ou de chiffrer), {pluriel(vue.priorites.important, 'important', 'importants')}, {pluriel(vue.priorites.complement, 'complément', 'compléments')}.
              {vue.priorites.complementsDecoches > 0 && c.status === 'brouillon'
                ? ` Pour un courrier court, ${pluriel(vue.priorites.complementsDecoches, 'complément est proposé décoché', 'compléments sont proposés décochés')} : cochez-les si vous souhaitez les poser.` : ''}
            </Alerte>
          )}
          {vue.sectionsInfo.map((s, i) => (
            <SectionCourrier key={s.cle} rang={i + 1} info={s} incluse={c.sections[s.cle] !== false} modifiable={modifiable}
              items={vue.items.filter((x) => x.section === s.cle)} courrierId={c.id} basculer={(v) => basculerSection(s.cle, v)} />
          ))}

          <Panneau titre="Conclusion" actions={modifiable && champs.conclusion !== vue.defauts.conclusion
            ? <Bouton compact variante="fantome" icone={<RotateCcw aria-hidden />} onClick={() => changer('conclusion', vue.defauts.conclusion)}>Texte par défaut</Bouton> : undefined}>
            <ChampTexte libelle="Paragraphe de conclusion" rows={6} value={champs.conclusion} readOnly={!modifiable} onChange={(e) => changer('conclusion', e.target.value)} />
          </Panneau>

          <Panneau titre="Signataire">
            <div className="grille-form">
              <Champ libelle="Nom du signataire" value={champs.signataireNom} readOnly={!modifiable} erreur={erreursEmission.find((e) => e.champ === 'signataireNom')?.message}
                onChange={(e) => changer('signataireNom', e.target.value)} />
              <Champ libelle="Fonction" value={champs.signataireQualite} readOnly={!modifiable} onChange={(e) => changer('signataireQualite', e.target.value)} />
            </div>
          </Panneau>
        </div>
      </div>
      {confirmation}
    </DossierCadre>
  );
}

function SectionCourrier({ rang, info, incluse, modifiable, items, courrierId, basculer }:
  { rang: number; info: { cle: string; titre: string; prefixe: string; chapeau: string }; incluse: boolean; modifiable: boolean; items: ItemCourrier[];
    courrierId: string; basculer: (v: boolean) => void }) {
  const retenus = items.filter((x) => x.numero).length;
  const [coche, setCoche] = useState(incluse);
  useEffect(() => { setCoche(incluse); }, [incluse]);
  return (
    <Panneau className={coche ? '' : 'co-section-exclue'} titre={`${rang}. ${info.titre}`}
      actions={<label className="case co-case-section"><input type="checkbox" checked={coche} disabled={!modifiable || !items.length}
        onChange={(e) => { setCoche(e.target.checked); basculer(e.target.checked); }} /><span className="petit">Inclure la section</span></label>}>
      <div className="pile">
        <p className="petit secondaire">{info.chapeau}</p>
        {items.length === 0 ? <p className="secondaire">Aucun point de ce type dans le dossier.</p> : (
          <>
            <p className="tres-petit discret">{incluse ? `${pluriel(retenus, 'point retenu', 'points retenus')} sur ${items.length}` : 'Section non incluse : aucun de ses points ne figure dans le courrier.'}</p>
            <ol className="co-items">
              {items.map((it) => <PointCourrier key={it.id} it={it} courrierId={courrierId} modifiable={modifiable} sectionIncluse={incluse} />)}
            </ol>
          </>
        )}
      </div>
    </Panneau>
  );
}

function PointCourrier({ it, courrierId, modifiable, sectionIncluse }: { it: ItemCourrier; courrierId: string; modifiable: boolean; sectionIncluse: boolean }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [texte, setTexte] = useState(it.texte);
  const [inclus, setInclus] = useState(it.inclus);
  useEffect(() => { setInclus(it.inclus); }, [it.inclus]);
  const [etat, setEtat] = useState<EtatEnregistrement>('enregistre');
  const minuterie = useRef<ReturnType<typeof setTimeout>>(undefined);
  const envoyer = async (corps: { texte?: string | null; inclus?: boolean }, rechargerTout = false) => {
    setEtat('en_cours');
    try {
      const r = await api.patch<{ texte: string; modifie: boolean; inclus: boolean }>(`/api/courriers/${courrierId}/items/${it.id}`, corps);
      setEtat('enregistre');
      if (corps.texte !== undefined && corps.texte === null) setTexte(r.texte);
      if (rechargerTout) await qc.invalidateQueries({ queryKey: ['courrier', courrierId] });
      else qc.setQueryData(['courrier', courrierId], (v: VueCourrier | undefined) => v && ({ ...v, items: v.items.map((x) => x.id === it.id ? { ...x, texte: r.texte, modifie: r.modifie } : x) }));
      return true;
    } catch (e) { setEtat('erreur'); notifier(messageErreur(e), true); return false; }
  };
  useEffect(() => () => clearTimeout(minuterie.current), []);
  // Hauteur ajustée au texte (toute la question visible, quelle que soit la largeur d'écran).
  const zone = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = zone.current;
    if (!el) return;
    const ajuster = () => { el.style.height = 'auto'; el.style.height = `${el.scrollHeight + 2}px`; };
    ajuster();
    // Réajuste seulement quand la LARGEUR change (rotation, fenêtre) : jamais de boucle d'observation.
    let largeur = el.clientWidth;
    const obs = new ResizeObserver(() => { if (el.clientWidth !== largeur) { largeur = el.clientWidth; requestAnimationFrame(ajuster); } });
    obs.observe(el);
    return () => obs.disconnect();
  }, [texte]);
  const idTexte = `co-texte-${it.id}`;
  return (
    <li className={`co-item ${inclus && sectionIncluse ? '' : 'co-item-exclu'}`}>
      <div className="co-item-tete">
        <label className="case">
          <input type="checkbox" checked={inclus} disabled={!modifiable} onChange={(e) => {
            const v = e.target.checked; setInclus(v);   // affichage immédiat ; la numérotation suit au rechargement
            void envoyer({ inclus: v }, true).then((ok) => { if (!ok) setInclus(!v); });
          }} />
          <span className="co-num">{it.numero ?? 'Non retenu'}</span>
        </label>
        <BadgePriorite priorite={it.priorite} motif={it.priorite_motif} />
        {it.ia && <Badge ton="ambre" titre="Point issu d’une proposition de l’assistance : à relire avant émission">Proposé par l’assistance — à vérifier</Badge>}
        {it.modifie && <Badge ton="info">Texte modifié</Badge>}
        {modifiable && etat !== 'enregistre' && <IndicateurEnregistrement etat={etat} />}
      </div>
      {it.priorite === 'complement' && it.priorite_motif && <p className="tres-petit discret co-motif">Complément : {it.priorite_motif}.</p>}
      <label className="visuellement-cache" htmlFor={idTexte}>Texte du point {it.numero ?? ''}</label>
      <textarea ref={zone} id={idTexte} className="saisie co-texte" rows={2} value={texte} readOnly={!modifiable} maxLength={4000}
        onChange={(e) => {
          const v = e.target.value; setTexte(v); setEtat('modifie');
          clearTimeout(minuterie.current);
          if (v.trim()) minuterie.current = setTimeout(() => { void envoyer({ texte: v }); }, 700);
        }} />
      {it.modifie && (
        <details className="co-origine">
          <summary className="petit">Texte d’origine (proposé à partir du dossier)</summary>
          <p className="petit secondaire">{it.texte_origine}</p>
          {modifiable && <Bouton compact variante="fantome" icone={<RotateCcw aria-hidden />} onClick={() => envoyer({ texte: null })}>Rétablir le texte d’origine</Bouton>}
        </details>
      )}
      {it.sources.length > 0 && (
        <ul className="co-sources" aria-label="Sources">
          {it.sources.map((s, k) => (
            <li key={k}>
              <LienSource versionId={s.version_id} page={s.page} compact
                titre={`${it.sources.length > 1 ? `Source ${k + 1} : ` : ''}${s.titre || TYPES_PIECE[s.doc_type ?? ''] || 'Pièce du dossier'}${s.version_label ? `, version ${s.version_label}` : ''}`}
                localisation={s.locator && s.locator !== 'demande saisie' ? s.locator : null} />
              <Extrait texte={s.excerpt} className="extrait petit" />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function PanneauEnvoi({ c, ecrire, recharger, rafraichirListes }: { c: Courrier; ecrire: boolean; recharger: () => Promise<void>; rafraichirListes: () => void }) {
  const notifier = useNotifier();
  const [envoi, setEnvoi] = useState({ envoyeLe: c.envoye_le ?? '', moyen: c.envoye_moyen ?? '', precision: c.envoye_precision ?? '' });
  const [enCours, setEnCours] = useState(false);
  const enregistrer = async () => {
    setEnCours(true);
    try {
      await api.patch(`/api/courriers/${c.id}/envoi`, { envoyeLe: envoi.envoyeLe || null, moyen: envoi.moyen || null, precision: envoi.precision || null });
      await recharger(); rafraichirListes(); notifier('Envoi enregistré.');
    } catch (e) { notifier(messageErreur(e), true); } finally { setEnCours(false); }
  };
  return (
    <Panneau titre="Envoi">
      <div className="pile" style={{ gap: 10 }}>
        <p className="petit secondaire">TRAME n’envoie aucun courriel : envoyez le PDF depuis votre messagerie ou par courrier, puis indiquez-le ici.</p>
        {c.envoye_le ? <Badge ton="succes">Envoyé le {date(c.envoye_le)}{c.envoye_moyen ? ` — ${MOYENS[c.envoye_moyen] ?? c.envoye_moyen}` : ''}</Badge>
          : <Badge ton="ambre">Envoi non renseigné</Badge>}
        {ecrire && (
          <>
            <Champ libelle="Envoyé le" type="date" value={envoi.envoyeLe} onChange={(e) => setEnvoi({ ...envoi, envoyeLe: e.target.value })} />
            <ChampChoix libelle="Moyen d’envoi" value={envoi.moyen} vide="À préciser" options={options(MOYENS)} onChange={(e) => setEnvoi({ ...envoi, moyen: e.target.value })} />
            <Champ libelle="Précision" aide="Destinataires en copie, n° de recommandé…" value={envoi.precision} onChange={(e) => setEnvoi({ ...envoi, precision: e.target.value })} />
            <Bouton icone={<Send aria-hidden />} chargement={enCours} disabled={!envoi.envoyeLe} onClick={enregistrer}>Enregistrer l’envoi</Bouton>
          </>
        )}
      </div>
    </Panneau>
  );
}

function AlerteCoordonnees({ modifiable, recharger }: { modifiable: boolean; recharger: () => Promise<void> }) {
  const notifier = useNotifier();
  const [ouvert, setOuvert] = useState(false);
  const [v, setV] = useState({ adresse: '', codePostal: '', ville: '', telephone: '', email: '', siret: '' });
  const [enCours, setEnCours] = useState(false);
  const enregistrer = async () => {
    setEnCours(true);
    try { await api.put('/api/organisation/coordonnees', v); setOuvert(false); await recharger(); notifier('Coordonnées de l’entreprise enregistrées.'); }
    catch (e) { notifier(messageErreur(e), true); } finally { setEnCours(false); }
  };
  return (
    <>
      <Alerte ton="ambre" titre="Coordonnées de l’entreprise non renseignées"
        action={modifiable ? <Bouton compact onClick={() => setOuvert(true)}>Renseigner</Bouton> : undefined}>
        L’en-tête du courrier indiquera qu’elles manquent. {modifiable ? 'Elles serviront à tous les courriers.' : 'Demandez à un administrateur de les renseigner.'}
      </Alerte>
      <Dialogue titre="Coordonnées de l’entreprise" ouvert={ouvert} fermer={() => setOuvert(false)}
        pied={<><Bouton onClick={() => setOuvert(false)}>Annuler</Bouton><Bouton variante="principal" chargement={enCours} onClick={enregistrer}>Enregistrer</Bouton></>}>
        <div className="pile">
          <p className="petit secondaire">Reprises dans l’en-tête de chaque courrier. Le nom de l’entreprise est celui de l’organisation.</p>
          <Champ libelle="Adresse" value={v.adresse} onChange={(e) => setV({ ...v, adresse: e.target.value })} />
          <div className="grille-form">
            <Champ libelle="Code postal" value={v.codePostal} onChange={(e) => setV({ ...v, codePostal: e.target.value })} />
            <Champ libelle="Ville" value={v.ville} onChange={(e) => setV({ ...v, ville: e.target.value })} />
            <Champ libelle="Téléphone" value={v.telephone} onChange={(e) => setV({ ...v, telephone: e.target.value })} />
            <Champ libelle="Adresse électronique" type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} />
            <Champ libelle="SIRET" value={v.siret} onChange={(e) => setV({ ...v, siret: e.target.value })} />
          </div>
        </div>
      </Dialogue>
    </>
  );
}
